/**
 * useJitaContext — fetches /api/markets/context (avg 30d) para una lista de
 * type_ids y computa delta_pct vs el current price que el caller ya tiene.
 *
 * Single endpoint, capped a 30 type_ids en el backend. Caller pasa un Map
 * con los current prices (los que ya tiene cargados del endpoint principal)
 * y el hook devuelve un Map con avg_30d + delta_pct por type_id.
 *
 * Caveat: ESI history es sell-order avg, no buy orders reales. El ISK exacto
 * puede mentir ~3-10%. El trend (delta_pct) sí es directionalmente correcto.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api'

export interface JitaContextEntry {
  avg_30d: number | null
  current_price: number | null
  delta_pct: number | null
  window_start: string | null
  window_end: string | null
}

export function useJitaContext(
  typeIds: number[],
  currentPrices: Map<number, number | null>,
) {
  const sorted = useMemo(
    () => [...new Set(typeIds.filter((n) => Number.isFinite(n) && n > 0))].sort((a, b) => a - b),
    [typeIds],
  )

  const { data, isLoading } = useQuery({
    queryKey: ['markets-context', sorted.join(',')],
    queryFn: () => api.marketsContext(sorted),
    enabled: sorted.length > 0,
    // History no cambia rápido — 30 min de stale time evita refetch en cada
    // navigación / filter change. RefreshButton del caller sigue forzando
    // (vía forceRefetchKey + force=true).
    staleTime: 1000 * 60 * 30,
  })

  const map = useMemo(() => {
    const out = new Map<number, JitaContextEntry>()
    if (!data) return out
    for (const ctx of data.contexts) {
      const current = currentPrices.get(ctx.type_id) ?? null
      const delta =
        ctx.avg_30d !== null && current !== null && current > 0
          ? ((current - ctx.avg_30d) / ctx.avg_30d) * 100
          : null
      out.set(ctx.type_id, {
        avg_30d: ctx.avg_30d,
        current_price: current,
        delta_pct: delta,
        window_start: ctx.window_start,
        window_end: ctx.window_end,
      })
    }
    return out
  }, [data, currentPrices])

  return { map, isLoading }
}